import React, { useState, useMemo } from 'react';
import { useAppData } from '@/context/AppContext';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import { Plus, Trash2, Edit2, Check, Info, ChevronDown, ChevronUp } from 'lucide-react';
import styles from './NutritionPage.module.css';

const QUICK_ADD_FOODS = [
  { name: 'Chicken Breast', protein: 31, carbs: 0, fats: 3.6, calories: 165, mealType: 'lunch' },
  { name: 'Greek Yogurt', protein: 10, carbs: 4, fats: 0, calories: 59, mealType: 'snack' },
  { name: 'Eggs (2)', protein: 12, carbs: 1, fats: 10, calories: 143, mealType: 'breakfast' },
  { name: 'Salmon', protein: 25, carbs: 0, fats: 13, calories: 208, mealType: 'dinner' },
  { name: 'Cottage Cheese', protein: 11, carbs: 3, fats: 4, calories: 98, mealType: 'snack' },
];

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack'] as const;

const DEFAULT_SUPPLEMENTS = [
  { name: 'Calcium', dosage: '500mg', icon: 'Ca', color: '#4CAF50' },
  { name: 'Vitamin D3', dosage: '2000 IU', icon: 'D3', color: '#FF9800' },
  { name: 'Magnesium', dosage: '200mg', icon: 'Mg', color: '#2196F3' },
  { name: 'Vitamin K2', dosage: '100mcg', icon: 'K2', color: '#9C27B0' },
  { name: 'Collagen', dosage: '10g', icon: 'Co', color: '#F44336' },
  { name: 'Omega-3', dosage: '1000mg', icon: 'O3', color: '#00BCD4' },
];

const NUTRITION_TIPS = [
  {
    title: 'Calcium for Bone Strength',
    content: 'Aim for 1000–1200mg of calcium daily. Best sources include dairy (milk, yogurt, cheese), fortified plant milks, leafy greens (kale, bok choy), and sardines with bones.',
    color: '#4CAF50',
  },
  {
    title: 'Vitamin D Supercharges Calcium',
    content: 'Without adequate Vitamin D, your body can only absorb about 10–15% of the calcium you eat. Get sunlight exposure daily and consider a D3 supplement of 1000–2000 IU.',
    color: '#FF9800',
  },
  {
    title: 'Protein Rebuilds Bone Matrix',
    content: 'Bone is ~35% protein. During fracture healing, your protein needs increase significantly. Aim for 1.2–1.6g per kg of body weight. Lean meats, eggs, legumes, and dairy are excellent sources.',
    color: '#B12612',
  },
  {
    title: 'Anti-inflammatory Foods',
    content: 'Reduce inflammation to speed healing. Focus on colourful vegetables, oily fish (salmon, mackerel), olive oil, berries, and turmeric. Limit processed foods, sugar, and alcohol.',
    color: '#9C27B0',
  },
  {
    title: 'Magnesium & Bone Health',
    content: 'Magnesium helps activate Vitamin D and is essential for bone formation. Good sources include almonds, pumpkin seeds, dark chocolate, spinach, and whole grains.',
    color: '#2196F3',
  },
  {
    title: 'Collagen Supports Healing',
    content: 'Collagen makes up the structural framework of bone. Vitamin C is essential for collagen synthesis — eat plenty of citrus, peppers, kiwi, and strawberries. Bone broth is also rich in collagen precursors.',
    color: '#D98A63',
  },
];

function getTodayString() {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

function getStreakForSupplement(supplementId: string, supplementLogs: any[]): number {
  let streak = 0;
  const today = new Date();
  let checkDate = new Date(today);
  checkDate.setDate(checkDate.getDate() - 1);
  while (true) {
    const dateStr = checkDate.getFullYear() + '-' + String(checkDate.getMonth() + 1).padStart(2, '0') + '-' + String(checkDate.getDate()).padStart(2, '0');
    const taken = supplementLogs.some(l => l.supplementId === supplementId && l.date === dateStr && l.taken);
    if (taken) {
      streak++;
      checkDate.setDate(checkDate.getDate() - 1);
    } else {
      break;
    }
  }
  return streak;
}

export default function NutritionPage() {
  const {
    meals, settings, supplements, supplementLogs,
    addMeal, updateMeal, deleteMeal,
    addSupplement, deleteSupplement, toggleSupplementLog,
  } = useAppData();

  const [showMealModal, setShowMealModal] = useState(false);
  const [editingMeal, setEditingMeal] = useState<any>(null);
  const [mealForm, setMealForm] = useState({ name: '', mealType: 'lunch', protein: '', carbs: '', fats: '', calories: '' });
  const [saving, setSaving] = useState(false);

  const [showSupplementModal, setShowSupplementModal] = useState(false);
  const [suppForm, setSuppForm] = useState({ name: '', dosage: '' });
  const [savingSupp, setSavingSupp] = useState(false);

  const [showAdvice, setShowAdvice] = useState(false);
  const [expandedTip, setExpandedTip] = useState<number | null>(null);

  const today = getTodayString();

  const todayMeals = useMemo(() => {
    return meals.filter((m: any) => {
      const d = m.timestamp instanceof Date ? m.timestamp : new Date(m.timestamp);
      const ds = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
      return ds === today;
    });
  }, [meals, today]);

  const macros = useMemo(() => {
    return todayMeals.reduce((acc: any, m: any) => ({
      protein: acc.protein + (m.protein || 0),
      carbs: acc.carbs + (m.carbs || 0),
      fats: acc.fats + (m.fats || 0),
      calories: acc.calories + (m.calories || 0),
    }), { protein: 0, carbs: 0, fats: 0, calories: 0 });
  }, [todayMeals]);

  const proteinGoal = Number(settings.dailyProteinGoal) || 120;
  const calorieGoal = Number(settings.dailyCalorieGoal) || 2000;

  const mealsByType = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    for (const type of MEAL_TYPES) {
      const filtered = todayMeals.filter((m: any) => m.mealType === type);
      if (filtered.length > 0) grouped[type] = filtered;
    }
    return grouped;
  }, [todayMeals]);

  const openAddMealModal = () => {
    setEditingMeal(null);
    setMealForm({ name: '', mealType: 'lunch', protein: '', carbs: '', fats: '', calories: '' });
    setShowMealModal(true);
  };

  const openEditMealModal = (meal: any) => {
    setEditingMeal(meal);
    setMealForm({
      name: meal.name,
      mealType: meal.mealType || 'lunch',
      protein: String(meal.protein || 0),
      carbs: String(meal.carbs || 0),
      fats: String(meal.fats || 0),
      calories: String(meal.calories || 0),
    });
    setShowMealModal(true);
  };

  const handleQuickAdd = async (food: typeof QUICK_ADD_FOODS[0]) => {
    setSaving(true);
    try {
      await addMeal({
        name: food.name,
        mealType: food.mealType,
        protein: food.protein,
        carbs: food.carbs,
        fats: food.fats,
        calories: food.calories,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleMealSubmit = async () => {
    if (!mealForm.name.trim()) return;
    setSaving(true);
    try {
      const data = {
        name: mealForm.name.trim(),
        mealType: mealForm.mealType,
        protein: Number(mealForm.protein) || 0,
        carbs: Number(mealForm.carbs) || 0,
        fats: Number(mealForm.fats) || 0,
        calories: Number(mealForm.calories) || 0,
      };
      if (editingMeal) {
        await updateMeal(editingMeal.id, data);
      } else {
        await addMeal(data);
      }
      setShowMealModal(false);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSupp = async (suppId: string) => {
    await toggleSupplementLog(suppId, today);
  };

  const handleAddSupplement = async () => {
    if (!suppForm.name.trim()) return;
    setSavingSupp(true);
    try {
      await addSupplement(suppForm.name.trim(), suppForm.dosage.trim(), suppForm.name.slice(0, 2).toUpperCase(), '#B12612');
      setSuppForm({ name: '', dosage: '' });
      setShowSupplementModal(false);
    } finally {
      setSavingSupp(false);
    }
  };

  const handleAddDefaultSupp = async (supp: typeof DEFAULT_SUPPLEMENTS[0]) => {
    await addSupplement(supp.name, supp.dosage, supp.icon, supp.color);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Nutrition</h1>
        <button className={styles.addBtn} onClick={openAddMealModal}>
          <Plus size={16} /> Add Meal
        </button>
      </div>

      <div className={styles.macroSummary}>
        {[
          { label: 'Protein', value: `${Math.round(macros.protein)}g`, color: 'var(--secondary)' },
          { label: 'Carbs', value: `${Math.round(macros.carbs)}g`, color: 'var(--accent)' },
          { label: 'Fats', value: `${Math.round(macros.fats)}g`, color: 'var(--tertiary)' },
          { label: 'Calories', value: `${Math.round(macros.calories)}`, color: 'var(--primary)' },
        ].map((item) => (
          <Card key={item.label}>
            <div className={styles.macroValue} style={{ color: item.color }}>{item.value}</div>
            <div className={styles.macroLabel}>{item.label}</div>
          </Card>
        ))}
      </div>

      <Card>
        <div className={styles.progressSection}>
          <div className={styles.progressItem}>
            <div className={styles.progressHeader}>
              <span className={styles.progressLabel}>Protein</span>
              <span className={styles.progressValue}>{Math.round(macros.protein)}g / {proteinGoal}g</span>
            </div>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{
                  width: `${Math.min(100, (macros.protein / proteinGoal) * 100)}%`,
                  background: 'var(--secondary)',
                }}
              />
            </div>
          </div>
          <div className={styles.progressItem}>
            <div className={styles.progressHeader}>
              <span className={styles.progressLabel}>Calories</span>
              <span className={styles.progressValue}>{Math.round(macros.calories)} / {calorieGoal}</span>
            </div>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{
                  width: `${Math.min(100, (macros.calories / calorieGoal) * 100)}%`,
                  background: 'var(--primary)',
                }}
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Supplements Section */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h2 className={styles.sectionTitle} style={{ marginBottom: 0 }}>Daily Supplements</h2>
          <button className={styles.addBtn} style={{ fontSize: 13, padding: '8px 14px' }} onClick={() => setShowSupplementModal(true)}>
            <Plus size={14} /> Add
          </button>
        </div>
        <Card>
          {supplements.length === 0 ? (
            <div>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 16 }}>
                No supplements added yet. Add bone-health supplements to track your daily intake.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
                {DEFAULT_SUPPLEMENTS.map(s => (
                  <button
                    key={s.name}
                    className={styles.addSupplementBtn}
                    onClick={() => handleAddDefaultSupp(s)}
                  >
                    <span style={{ fontWeight: 700, color: s.color }}>{s.icon}</span>
                    <span style={{ fontSize: 13 }}>{s.name}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className={styles.supplementList}>
              {supplements.map((supp: any) => {
                const isTaken = supplementLogs.some(l => l.supplementId === supp.id && l.date === today && l.taken);
                const streak = getStreakForSupplement(supp.id, supplementLogs);
                return (
                  <div key={supp.id} className={styles.supplementItem}>
                    <button
                      className={`${styles.supplementCheckbox} ${isTaken ? styles.supplementCheckboxChecked : ''}`}
                      onClick={() => handleToggleSupp(supp.id)}
                      style={isTaken ? {} : { borderColor: supp.color || 'var(--border)' }}
                    >
                      {isTaken ? <Check size={13} /> : null}
                    </button>
                    <div
                      style={{
                        width: 36,
                        height: 36,
                        borderRadius: 8,
                        background: (supp.color || '#B12612') + '22',
                        color: supp.color || '#B12612',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 11,
                        flexShrink: 0,
                      }}
                    >
                      {supp.icon || supp.name.slice(0, 2).toUpperCase()}
                    </div>
                    <div className={styles.supplementInfo}>
                      <div className={styles.supplementName} style={{ textDecoration: isTaken ? 'line-through' : 'none', opacity: isTaken ? 0.7 : 1 }}>
                        {supp.name}
                      </div>
                      {supp.dosage ? <div className={styles.supplementDosage}>{supp.dosage}</div> : null}
                    </div>
                    {streak > 0 ? (
                      <div className={styles.supplementStreak}>{streak}d streak</div>
                    ) : null}
                    <button
                      style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', padding: 4, cursor: 'pointer', opacity: 0.6 }}
                      onClick={() => deleteSupplement(supp.id)}
                      title="Remove supplement"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
              <button className={styles.addSupplementBtn} onClick={() => setShowSupplementModal(true)}>
                <Plus size={14} /> Add supplement
              </button>
            </div>
          )}
        </Card>
      </div>

      {/* Today's Meals */}
      <div>
        <h2 className={styles.sectionTitle}>Today's Meals</h2>
        {Object.keys(mealsByType).length === 0 ? (
          <div className={styles.emptyMeals}>No meals logged today. Add a meal or use quick-add below.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {MEAL_TYPES.filter((t) => mealsByType[t]).map((type) => (
              <div key={type} className={styles.mealGroup}>
                <div className={styles.mealGroupTitle}>{type}</div>
                {mealsByType[type].map((meal: any) => (
                  <div key={meal.id} className={styles.mealItem}>
                    <div className={styles.mealInfo}>
                      <div className={styles.mealName}>{meal.name}</div>
                      <div className={styles.mealMacros}>
                        <span>P: {meal.protein}g</span>
                        <span>C: {meal.carbs}g</span>
                        <span>F: {meal.fats}g</span>
                      </div>
                    </div>
                    <div className={styles.mealCalories}>{meal.calories} cal</div>
                    <div className={styles.mealActions}>
                      <button className={styles.iconBtn} onClick={() => openEditMealModal(meal)}>
                        <Edit2 size={14} />
                      </button>
                      <button className={`${styles.iconBtn} ${styles.deleteBtn}`} onClick={() => deleteMeal(meal.id)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Add */}
      <div>
        <h2 className={styles.sectionTitle}>Quick Add - High Protein</h2>
        <div className={styles.quickAddGrid}>
          {QUICK_ADD_FOODS.map((food) => (
            <button
              key={food.name}
              className={styles.quickAddItem}
              onClick={() => handleQuickAdd(food)}
              disabled={saving}
            >
              <div className={styles.quickAddName}>{food.name}</div>
              <div className={styles.quickAddMacros}>
                {food.protein}g P / {food.carbs}g C / {food.fats}g F / {food.calories} cal
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Bone Health Advice */}
      <div>
        <button
          onClick={() => setShowAdvice(v => !v)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            marginBottom: showAdvice ? 12 : 0,
            width: '100%',
            textAlign: 'left',
          }}
        >
          <Info size={18} color="var(--secondary)" />
          <h2 className={styles.sectionTitle} style={{ marginBottom: 0, flex: 1 }}>Bone Health Nutrition Guide</h2>
          {showAdvice ? <ChevronUp size={18} color="var(--text-secondary)" /> : <ChevronDown size={18} color="var(--text-secondary)" />}
        </button>
        {showAdvice ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {NUTRITION_TIPS.map((tip, i) => (
              <Card key={i}>
                <button
                  onClick={() => setExpandedTip(expandedTip === i ? null : i)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    width: '100%',
                    textAlign: 'left',
                  }}
                >
                  <div style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: tip.color,
                    flexShrink: 0,
                  }} />
                  <span style={{ flex: 1, fontWeight: 600, fontSize: 15, color: 'var(--text)' }}>{tip.title}</span>
                  {expandedTip === i ? <ChevronUp size={16} color="var(--text-secondary)" /> : <ChevronDown size={16} color="var(--text-secondary)" />}
                </button>
                {expandedTip === i ? (
                  <p style={{ margin: '12px 0 0 22px', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                    {tip.content}
                  </p>
                ) : null}
              </Card>
            ))}
          </div>
        ) : null}
      </div>

      {/* Add Meal Modal */}
      <Modal isOpen={showMealModal} onClose={() => setShowMealModal(false)} title={editingMeal ? 'Edit Meal' : 'Add Meal'}>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Name</label>
          <input
            className={styles.formInput}
            value={mealForm.name}
            onChange={(e) => setMealForm({ ...mealForm, name: e.target.value })}
            placeholder="e.g. Grilled Chicken Salad"
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Meal Type</label>
          <select
            className={styles.formSelect}
            value={mealForm.mealType}
            onChange={(e) => setMealForm({ ...mealForm, mealType: e.target.value })}
          >
            {MEAL_TYPES.map((t) => (
              <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
            ))}
          </select>
        </div>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Protein (g)</label>
            <input
              className={styles.formInput}
              type="number"
              value={mealForm.protein}
              onChange={(e) => setMealForm({ ...mealForm, protein: e.target.value })}
              placeholder="0"
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Carbs (g)</label>
            <input
              className={styles.formInput}
              type="number"
              value={mealForm.carbs}
              onChange={(e) => setMealForm({ ...mealForm, carbs: e.target.value })}
              placeholder="0"
            />
          </div>
        </div>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Fats (g)</label>
            <input
              className={styles.formInput}
              type="number"
              value={mealForm.fats}
              onChange={(e) => setMealForm({ ...mealForm, fats: e.target.value })}
              placeholder="0"
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Calories</label>
            <input
              className={styles.formInput}
              type="number"
              value={mealForm.calories}
              onChange={(e) => setMealForm({ ...mealForm, calories: e.target.value })}
              placeholder="0"
            />
          </div>
        </div>
        <button className={styles.submitBtn} onClick={handleMealSubmit} disabled={saving || !mealForm.name.trim()}>
          {saving ? 'Saving...' : editingMeal ? 'Update Meal' : 'Add Meal'}
        </button>
      </Modal>

      {/* Add Supplement Modal */}
      <Modal isOpen={showSupplementModal} onClose={() => setShowSupplementModal(false)} title="Add Supplement">
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Supplement Name</label>
          <input
            className={styles.formInput}
            value={suppForm.name}
            onChange={(e) => setSuppForm({ ...suppForm, name: e.target.value })}
            placeholder="e.g. Vitamin D3"
            autoFocus
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Dosage (optional)</label>
          <input
            className={styles.formInput}
            value={suppForm.dosage}
            onChange={(e) => setSuppForm({ ...suppForm, dosage: e.target.value })}
            placeholder="e.g. 1000 IU, 500mg"
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>Or add a common bone-health supplement</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 4 }}>
            {DEFAULT_SUPPLEMENTS.map(s => (
              <button
                key={s.name}
                className={styles.addSupplementBtn}
                style={{ flexDirection: 'column', padding: '10px 8px', gap: 4 }}
                onClick={async () => {
                  await handleAddDefaultSupp(s);
                  setShowSupplementModal(false);
                }}
              >
                <span style={{ fontWeight: 700, fontSize: 13, color: s.color }}>{s.icon}</span>
                <span style={{ fontSize: 11 }}>{s.name}</span>
              </button>
            ))}
          </div>
        </div>
        <button
          className={styles.submitBtn}
          onClick={handleAddSupplement}
          disabled={savingSupp || !suppForm.name.trim()}
        >
          {savingSupp ? 'Adding...' : 'Add Supplement'}
        </button>
      </Modal>
    </div>
  );
}
